import apiClient from '@/lib/axios'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

export interface Restaurant {
  id: string
  name: string
  email?: string | null
  phone?: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateRestaurantInput {
  name: string
  email?: string
  phone?: string
}

export interface UpdateRestaurantInput {
  name?: string
  email?: string
  phone?: string
}

/**
 * Hook to fetch all restaurants (super admin only)
 * Returns empty array for non-super-admin users
 */
export function useRestaurants() {
  const { getToken } = useAuth()

  console.log('[useRestaurants] Hook called')

  const queryResult = useQuery<Restaurant[]>({
    queryKey: ['restaurants'],
    queryFn: async () => {
      console.log('[useRestaurants] Query function started')
      try {
        const token = await getToken()
        console.log('[useRestaurants] Token obtained:', token ? 'Yes' : 'No')
        if (!token) {
          throw new Error('No authentication token available')
        }

        console.log('[useRestaurants] Making API request to /restaurants')
        const response = await apiClient.get<Restaurant[]>('/restaurants', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        console.log('[useRestaurants] API response received:', {
          status: response.status,
          dataLength: response.data?.length || 0,
          data: response.data,
        })

        return response.data
      } catch (error) {
        const axiosError = error as AxiosError
        console.error('[useRestaurants] Error occurred:', {
          message: axiosError?.message,
          status: axiosError?.response?.status,
          statusText: axiosError?.response?.statusText,
          data: axiosError?.response?.data,
          fullError: error,
        })
        // If 403, user is not super admin - return empty array
        if (axiosError.response?.status === 403) {
          console.log('[useRestaurants] 403 Forbidden - returning empty array (not super admin)')
          return []
        } else {
          console.log('[useRestaurants] Re-throwing error')
        }
        throw error
      }
    },
    staleTime: 300000, // 5 minutes
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const axiosError = error as AxiosError
      console.log('[useRestaurants] Retry check:', {
        failureCount,
        errorStatus: axiosError?.response?.status,
        willRetry: axiosError?.response?.status !== 403 && failureCount < 2,
      })
      // Don't retry on 403 errors (not super admin)
      if (axiosError?.response?.status === 403) {
        return false
      }
      return failureCount < 2
    },
  })

  // Debug query result (this will log on every render when query state changes)
  if (queryResult.data !== undefined || queryResult.error !== undefined) {
    console.log('[useRestaurants] Query result:', {
      isLoading: queryResult.isLoading,
      isError: queryResult.isError,
      isSuccess: queryResult.isSuccess,
      data: queryResult.data,
      error: queryResult.error,
      dataLength: queryResult.data?.length || 0,
    })
  }

  const queryClient = useQueryClient()

  const createRestaurant = useMutation({
    mutationFn: async (data: CreateRestaurantInput) => {
      const token = await getToken()
      const response = await apiClient.post<Restaurant>('/restaurants', data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurants'] })
    },
  })

  const updateRestaurant = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRestaurantInput }) => {
      const token = await getToken()
      const response = await apiClient.patch<Restaurant>(`/restaurants/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurants'] })
    },
  })

  const deleteRestaurant = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      await apiClient.delete(`/restaurants/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurants'] })
    },
  })

  return {
    ...queryResult,
    createRestaurant,
    updateRestaurant,
    deleteRestaurant,
  }
}
